// ----------------------------------------------------------------------
// Route metadata — the single place that knows how the public URL space maps
// to human-readable labels, and which routes intentionally render without the
// site chrome. Consumed by <SiteChrome /> and by the breadcrumb system.
// ----------------------------------------------------------------------

import { humanizeSlug } from "@/lib/utils";
import type { IBreadcrumbItem } from "@/types/breadcrumb";

/**
 * Full-screen experiences rendered without the top banner / footer: they own
 * their whole viewport and must not receive breadcrumbs either.
 */
const CHROMELESS_PREFIXES = [
  "/quiz",
  "/feedback",
  "/login",
  "/contact"
  // "/organisme-de-formation",
] as const;

/** Standalone printable leaves, matched on the tail of the path (e.g. /formation/:slug/programme). */
const CHROMELESS_SUFFIXES = ["/programme"] as const;

/**
 * True for routes that opt out of the shared chrome (banner, footer, breadcrumbs).
 * Kept here so the layout and the breadcrumb builder can never drift apart.
 */
export function isChromelessPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;

  if (CHROMELESS_SUFFIXES.some((suffix) => pathname.endsWith(suffix))) return true;

  return CHROMELESS_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Routes that keep the footer but drop the top phone banner (the page already shows contact details). */
const NO_TOP_BANNER_PATHS = ["/contact"] as const;

export function hidesTopBanner(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return (NO_TOP_BANNER_PATHS as readonly string[]).includes(pathname);
}

// ----------------------------------------------------------------------
// Breadcrumbs
// ----------------------------------------------------------------------

/** First crumb of every trail. */
export const HOME_CRUMB: IBreadcrumbItem = { label: "Accueil", href: "/" };

/**
 * Labels for the static parts of the URL space, keyed by full path.
 *
 * `href` is only set when the segment has no page of its own and the crumb has
 * to point somewhere else — "/formation" is a detail-only namespace, so its parent
 * crumb links to the catalog at "/formations".
 */
const ROUTE_CRUMBS: Record<string, { label: string; href?: string }> = {
  "/formations": { label: "Nos formations" },
  "/formation": { label: "Nos formations", href: "/formations" },
  "/actualites": { label: "Actualités" },
  "/entrepreneurship": { label: "Entreprenariat" },
  "/coaching-professionnel": { label: "Coaching professionnel" },
  "/coaching-manager": { label: "Coaching Manager" },
  "/coaching-equipe": { label: "Coaching d'équipe" },
  "/formation-sur-mesure": { label: "Formation sur-mesure" },
  "/formation-inter-entreprise": { label: "Formation inter-entreprise" },
  "/formation-intra-entreprise": { label: "Formation intra-entreprise" },
  "/apropos": { label: "À propos" },
  "/methode": { label: "Notre méthode" },
  "/avis": { label: "Avis & Témoignages" },
  "/enseigner": { label: "Devenir formateur" },
  "/contact": { label: "Contact" },
  "/favoris": { label: "Mes favoris" },
};

/**
 * Crumb for a known static route, ready to be spliced into a manually built
 * trail — keeps labels for shared ancestors ("Nos formations", "Entreprenariat")
 * defined in exactly one place.
 */
export function routeCrumb(path: string): IBreadcrumbItem {
  const route = ROUTE_CRUMBS[path];

  return {
    label: route?.label ?? humanizeSlug(path.split("/").filter(Boolean).at(-1) ?? ""),
    href: route?.href ?? path,
  };
}

export interface BuildBreadcrumbsOptions {
  /**
   * Label overrides for dynamic segments, keyed either by the raw segment
   * ("marketing-digital") or by its full path ("/formations/marketing-digital").
   * Use it when a real name (category, news type) is only known at runtime.
   */
  labels?: Record<string, string>;
  /** Shortcut override for the last crumb — handy when only the page title is known. */
  currentLabel?: string;
}

/**
 * Derives the breadcrumb trail from a pathname.
 *
 * Returns an empty array where breadcrumbs don't apply (home page, chrome-less
 * routes), so callers can drop <Breadcrumb /> anywhere without guarding.
 */
export function buildBreadcrumbs(
  pathname: string | null | undefined,
  { labels, currentLabel }: BuildBreadcrumbsOptions = {},
): IBreadcrumbItem[] {
  if (!pathname || pathname === "/" || isChromelessPath(pathname)) return [];

  const segments = pathname.split("/").filter(Boolean);
  if (!segments.length) return [];

  const items: IBreadcrumbItem[] = [HOME_CRUMB];
  let path = "";

  segments.forEach((segment, index) => {
    path += `/${segment}`;
    const isLast = index === segments.length - 1;
    const route = ROUTE_CRUMBS[path];

    const label =
      (isLast ? currentLabel : undefined) ??
      labels?.[segment] ??
      labels?.[path] ??
      route?.label ??
      humanizeSlug(segment);

    items.push({
      label,
      // The current page is never a link.
      href: isLast ? undefined : (route?.href ?? path),
    });
  });

  return items;
}
